import { Subject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

export class AuthFlowState {
  event$ = new Subject<AuthFlowStateEvent>();

  private _id: string = uuidv4();
  private _flowType: FlowType;
  private _type: AuthType;
  private _currentScreenHeader: ScreenType;
  private _currentScreen: ScreenType;
  private _headerText: string;
  private _titleText: string;
  private _descriptionText: string;
  private _nextBtnText: string;
  private _illustration: PincodeIllustration;
  private _showForgotPin = false;
  private _showSkipBtn = false;
  private _showBackBtn = true;
  private _screenCategory: ScreenCategory;
  private _status: PincodeNativeStatus;
  private _clearValidatePin = false;
  private _showResetLink = false;
  private _showNextBtn = true;
  private _temporary = false; // true means the state would be deleted after user is navigated to other state
  private _resetLinkSentCount = 0;

  static create(flowType: FlowType, type: AuthType, currentScreenHeader: ScreenType, currentScreen: ScreenType): AuthFlowState {
    const state = new AuthFlowState();
    state._flowType = flowType;
    state._type = type;
    state._currentScreenHeader = currentScreenHeader;
    state._currentScreen = currentScreen;
    state.setProperties();
    return state;
  }

  setProperties() {
    switch (this.currentScreenHeader) {
      case ScreenType.login:
        this._headerText = 'PinPage.enterPin';
        break;
      case ScreenType.create:
        this._headerText = 'PinPage.pinSetup';
        break;
      case ScreenType.change:
        this._headerText = 'SettingsPage.changePin';
        break;
      case ScreenType.reset:
        this._headerText = 'SuspendPage.pincodeReset';
        break;
    }

    switch (this.currentScreen) {
      case ScreenType.login:
        if (this._flowType === FlowType.change) {
          this._titleText = 'ResetPage.loginPin';
        } else {
          this._titleText = 'PinPage.enterYourPin';
          this._showBackBtn = false;
        }
        this._showForgotPin = true;
        this._screenCategory = ScreenCategory.enterPin;
        break;
      case ScreenType.optional:
        if (this._flowType === FlowType.setup) {
          this._titleText = 'OptionalPage.pincodeEnable';
          this._screenCategory = ScreenCategory.optional;
          this._descriptionText = 'OptionalPage.optionalDescription';
          this._illustration = PincodeIllustration.pincodeAuth;
          this._nextBtnText = 'OptionalPage.pincodeSetup';
          this._showSkipBtn = true;
          this._showBackBtn = false;
        }
        break;
      case ScreenType.extra:
        if (this._flowType === FlowType.setup) {
          this._titleText = 'OptionalPage.pincodeEnable';
          this._screenCategory = ScreenCategory.optional;
          this._descriptionText = 'OptionalPage.pincodeDescription2';
          this._illustration = PincodeIllustration.pincodeAuth;
          this._nextBtnText = 'OptionalPage.pincodeSetup';
          this._showSkipBtn = false;
        }
        break;
      case ScreenType.create:
        this._titleText = 'PinPage.createPin';
        this._screenCategory = ScreenCategory.enterPin;
        this._status = this.flowType === FlowType.reset ? PincodeNativeStatus.resetPrompted : PincodeNativeStatus.prompted;
        if (this._flowType === FlowType.reset) {
          this._showBackBtn = false;
        }
        break;
      case ScreenType.change:
        this._titleText = 'ResetPage.createPin';
        this._screenCategory = ScreenCategory.enterPin;
        break;
      case ScreenType.validate:
        this._titleText = this.flowType === FlowType.change ? 'PinPage.validatePinChange' : 'PinPage.validatePinCreate';
        this._screenCategory = ScreenCategory.enterPin;
        this._clearValidatePin = true;
        break;
      case ScreenType.success:
        this._titleText = this.flowType === FlowType.change ? 'SuccessPage.changePinSuccess' : 'SuccessPage.pincodeSuccess';
        this._screenCategory = ScreenCategory.optional;
        this._descriptionText =
          this.flowType === FlowType.change ? 'SuccessPage.changePinSuccessDescription' : 'SuccessPage.pincodeDescription';
        this._illustration = PincodeIllustration.successAuth;
        this._nextBtnText = 'CommonTexts.continue';
        this._showBackBtn = false;
        this._clearValidatePin = true;
        break;
      case ScreenType.locked:
        this._screenCategory = ScreenCategory.optional;
        this._illustration = PincodeIllustration.pincodeAuth;
        this._titleText = 'SuspendPage.accountLocked';
        this._descriptionText = 'SuspendPage.description';
        this._showBackBtn = false;
        this._showResetLink = true;
        this._showNextBtn = false;
        break;
      case ScreenType.reset:
        this._screenCategory = ScreenCategory.optional;
        this._illustration = PincodeIllustration.pincodeAuth;
        this._titleText = this.resetLinkSentCount <= 1 ? 'SuspendPage.accountReset' : 'SuspendPage.sentNewLink';
        this._descriptionText = 'SuspendPage.checkEmail';
        this._showResetLink = true;
        this._showNextBtn = false;
        this._temporary = true;
        break;
    }
  }
  get id(): string {
    return this._id;
  }
  get flowType(): FlowType {
    return this._flowType;
  }
  get type(): AuthType {
    return this._type;
  }
  get currentScreenHeader(): ScreenType {
    return this._currentScreenHeader;
  }
  get headerText(): string {
    return this._headerText;
  }
  get titleText(): string {
    return this._titleText;
  }
  get descriptionText(): string {
    return this._descriptionText;
  }
  get showForgotPin(): boolean {
    return this._showForgotPin;
  }
  get screenCategory(): ScreenCategory {
    return this._screenCategory;
  }
  get currentScreen(): ScreenType {
    return this._currentScreen;
  }
  get illustration(): PincodeIllustration {
    return this._illustration;
  }
  get nextBtnText(): string {
    return this._nextBtnText;
  }
  get showSkipBtn(): boolean {
    return this._showSkipBtn;
  }
  get showBackBtn(): boolean {
    return this._showBackBtn;
  }
  get status(): PincodeNativeStatus {
    return this._status;
  }
  get clearValidatePin(): boolean {
    return this._clearValidatePin;
  }
  get showResetLink(): boolean {
    return this._showResetLink;
  }
  get showNextBtn(): boolean {
    return this._showNextBtn;
  }
  get temporary(): boolean {
    return this._temporary;
  }
  get resetLinkSentCount(): number {
    return this._resetLinkSentCount;
  }
  set resetLinkSentCount(value: number) {
    this._resetLinkSentCount = value;
    this.setProperties();
  }
}

export enum AuthWorkFlow {
  loginPin = 'loginPin', // unlock the screen with pin code
  loginNative = 'loginNative', // unlock the screen with native security
  setup = 'setup', // create pin first time when user is logged in
  setupFromSetting = 'setupFromSetting', // create pin from setting page
  change = 'change', // change the existing pin (user already has pin before and wants to change it)
  reset = 'reset', // reset the pin (user forgot the pin and wants to reset it)
  locked = 'locked', // user is locked and needs to reset the pin
}

export enum AuthType {
  native = 'native',
  pincode = 'pincode',
}

export enum ScreenType {
  optional = 'optional',
  extra = 'extra',
  create = 'create',
  validate = 'validate',
  login = 'login',
  success = 'success',
  locked = 'locked',
  reset = 'reset',
  change = 'change',
}

export enum FlowType {
  setup = 'setup',
  login = 'login',
  change = 'change',
  reset = 'reset',
  validate = 'validate',
  locked = 'locked',
}

export enum ScreenCategory {
  enterPin = 'enterPin',
  optional = 'optional',
  success = 'success',
}

export enum PincodeStatus {
  set = 'set',
  unset = 'unset',
  skipped = 'skipped',
  blocked = 'blocked',
  prompted = 'prompted',
}

export enum PincodeIllustration {
  pincodeAuth = 'pincode-auth',
  nativeAuth = 'native-auth',
  successAuth = 'success-auth',
}

export interface AuthFlowStateEvent {
  type: AuthFlowStateEventType;
  data?: any;
}

export enum AuthFlowStateEventType {
  clearPin = 'clearPin',
  forgotPin = 'forgotPin',

  finished = 'finished', // when the auth flow is finished
  nextState = 'nextState', // when moving to the next state
  prevState = 'prevState', // when moving to the previous state

  updateWorkflow = 'updateWorkflow', // when the workflow is need to be updated
}

export enum AuthResult {
  doesntMatch = 'doesntMatch',
  noError = 'noError',
  incorrectPin = 'incorrectPin',
  serverError = 'serverError',
}

export enum PincodeNativeStatus {
  enabled = 'ENABLED',
  skipped = 'SKIPPED',
  prompted = 'PROMPTED',
  resetPrompted = 'RESET_PROMPTED',
}
